import { useEffect } from 'react';
import get from 'lodash/get';
import useLocalStorage from 'chameleon/ui-stack/ui/base/hooks/use-local-storage';
import log from 'chameleon/ui-stack/utilities/log';

const usePersistFilters = ({
  subject,
  userId,
  router,
  listenFilterChange = false,
} = {}) => {
  const [saveItemToLocalStorage, clearSavedFilters, getSavedFilters] =
    useLocalStorage({
      key: `tags`,
      itemKey: `${userId}-${subject}`,
    });
  const savedFilters = getSavedFilters();
  const arePersistedFiltersLoaded = !!savedFilters;
  const tags = get(router, 'location.query.tags');
  const excludeHigher = get(router, 'location.query.excludeHigher');
  const includeOnly = get(router, 'location.query.includeOnly');
  const searchTerm = get(router, 'location.query.searchTerm');
  const sortTagsBy = get(router, 'location.query.sortTagsBy');
  const filterStatus = get(router, 'location.query.filterStatus');

  useEffect(() => {
    if (
      listenFilterChange &&
      (tags || searchTerm || sortTagsBy || filterStatus)
    ) {
      let filters = {
        tags,
        searchTerm,
        includeOnly,
        excludeHigher,
        sortTagsBy,
        filterStatus,
      };
      if (savedFilters !== filters) {
        doPersistFilters(JSON.stringify(filters));
      }
    }
  }, [
    listenFilterChange,
    tags,
    excludeHigher,
    savedFilters,
    searchTerm,
    includeOnly,
    sortTagsBy,
    filterStatus,
  ]);

  useEffect(() => {
    const filtersQuery = tags || searchTerm || sortTagsBy || filterStatus;
    if (listenFilterChange && arePersistedFiltersLoaded && !filtersQuery) {
      clearSavedFilters();
    }
  }, [
    listenFilterChange && arePersistedFiltersLoaded,
    tags,
    searchTerm,
    sortTagsBy,
    filterStatus,
  ]);

  const doPersistFilters = (tags) => {
    if (tags) {
      saveItemToLocalStorage(tags);
    } else if (savedFilters) {
      clearSavedFilters();
    }
  };

  const redirectWithFilters = () => {
    const {
      location: { query, pathname, state },
    } = router;
    const { topicQuestionsFilter, tagId } = state || {};

    let nextQuery = {
      ...query,
    };
    const noIncomingExtraFilters =
      !(topicQuestionsFilter || []).length && !tagId;
    const noSearchTerms = !query.searchTerm;
    let parsedSavedFilters = {};
    try {
      parsedSavedFilters = { ...JSON.parse(savedFilters) };
    } catch (error) {
      log('Error parsing previous saved tags', error);
    }

    if (noIncomingExtraFilters && !query.tags && noSearchTerms) {
      nextQuery = { ...nextQuery, ...parsedSavedFilters };
    } else {
      // always keep the sortTagsBy when navigating to the QB page from any other places
      const savedSortTagsBy = get(parsedSavedFilters, 'sortTagsBy');
      if (savedSortTagsBy) {
        nextQuery = { ...nextQuery, sortTagsBy: savedSortTagsBy };
      }
    }

    router.push({
      pathname,
      query: {
        ...nextQuery,
      },
      state,
    });
  };

  return [
    {
      arePersistedFiltersLoaded,
      redirectWithFilters,
      doPersistFilters,
    },
  ];
};

export default usePersistFilters;
